import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, Users, Shield, Tag, Plus, Pencil, Check, ChevronDown, ArrowUpDown, AlertCircle, AlertTriangle, X, Eye, EyeOff, Lock } from 'lucide-react';
import { API_URL } from '../config';
import Navbar from './Navbar';

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด", icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // eslint-disable-next-line eqeqeq
    const selectedOption = options.find(opt => opt.value == value);

    return (
        <div className="w-full md:w-auto min-w-[140px] relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left ${isOpen ? '' : ''
                    }`}
            >
                {Icon && (
                    <div className={`p-1 rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-blue-100' : 'bg-blue-50'}`}>
                        <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                )}
                <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-1">{label}</span>
                    <span className="text-xs font-bold text-slate-800">
                        {selectedOption?.label || allLabel}
                    </span>
                </div>
                <div className={`text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown className="h-3 w-3" />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-[60] mt-1.5 min-w-full w-max max-w-[240px] left-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                    <button
                        onClick={() => { onChange('All'); setIsOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${(!value || value === 'All') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <span>{allLabel}</span>
                        {(!value || value === 'All') && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    <div className="max-h-[200px] overflow-y-auto scrollbar-hide">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                // eslint-disable-next-line eqeqeq
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${value == opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {/* eslint-disable-next-line eqeqeq */}
                                {value == opt.value && <Check className="w-3.5 h-3.5" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const UserManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ username: '', password: '', confirmPassword: '', role: 'User', display_name: '', is_active: true });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);


    const [editingUser, setEditingUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [changePassword, setChangePassword] = useState(false);
    const isFirefox = typeof window !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

    // Filters State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-20" />;
        return sortConfig.direction === 'asc' ?
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600 rotate-180" /> :
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600" />;
    };

    const fetchUsers = React.useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 403) {
                const msg = err.response.data?.message || err.response.data?.error || 'Access Denied';
                setErrorMessage(msg);
                setShowErrorPopup(true);
            }
        }
    }, [token]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterRole, filterStatus, rowsPerPage]);

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');

        const trimmedDisplayName = (newUser.display_name || '').trim();
        const trimmedUsername = (newUser.username || '').trim();

        // 1. Check for multiple missing fields first
        const missingFields = [];
        if (!trimmedDisplayName) missingFields.push('DisplayName');
        if (!trimmedUsername) missingFields.push('Username');

        if (!editingUser || changePassword) {
            if (!newUser.password) missingFields.push('Password');
            if (!newUser.confirmPassword) missingFields.push('ConfirmPassword');
        }

        if (missingFields.length > 1) {
            setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนกดบันทึก');
            return;
        }

        // 2. Individual field validations if only one or specific rule failed
        if (!trimmedDisplayName) {
            setError('กรุณาระบุชื่อที่แสดง (Display Name)');
            return;
        }

        if (!trimmedUsername) {
            setError('กรุณาระบุ Username');
            return;
        }

        if (trimmedUsername.length < 4) {
            setError('Username ต้องมีความยาวอย่างน้อย 4 ตัวอักษร');
            return;
        }

        // Case-specific validations
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;

        if (!editingUser || changePassword) {
            // New user case or edit user with password change
            if (!newUser.password) {
                setError('กรุณาระบุรหัสผ่าน');
                return;
            }
            if (!passwordRegex.test(newUser.password)) {
                setError('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร, ประกอบด้วยตัวพิมพ์ใหญ่, ตัวพิมพ์เล็ก และอักขระพิเศษ อย่างละ 1 ตัว');
                return;
            }
            if (!newUser.confirmPassword) {
                setError('กรุณาระบุยืนยันรหัสผ่าน');
                return;
            }
            if (newUser.password !== newUser.confirmPassword) {
                setError('รหัสผ่านและการยืนยันรหัสผ่านไม่ตรงกัน');
                return;
            }
        }

        try {
            let userPayload = {
                ...newUser,
                username: trimmedUsername,
                display_name: trimmedDisplayName
            };
            delete userPayload.confirmPassword;

            if (editingUser) {
                // If password is blank during edit, don't send it to the server
                if (!userPayload.password) {
                    delete userPayload.password;
                }

                await axios.put(`${API_URL}/api/users/${editingUser.id}`, userPayload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`อัปเดตผู้ใช้งาน ${trimmedUsername} เรียบร้อยแล้ว`);
            } else {
                await axios.post(`${API_URL}/api/users`, userPayload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`สร้างผู้ใช้งาน ${trimmedUsername} เรียบร้อยแล้ว`);
            }

            setShowSuccessPopup(true);
            setIsModalOpen(false);
            fetchUsers();
        } catch (err) {
            const errorData = err.response?.data;
            let errorMsg = typeof errorData === 'string'
                ? errorData
                : (errorData?.message || errorData?.error || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');

            // Translate common backend errors to Thai
            if (errorMsg.includes('User already exist')) {
                errorMsg = 'Username นี้มีอยู่แล้วในระบบ กรุณาใช้ชื่ออื่น';
            } else if (errorMsg.includes('Unauthorized')) {
                errorMsg = 'เซสชั่นหมดอายุ กรุณาเข้าสู่ระบบใหม่';
            }

            setError(errorMsg);
        }
    };



    const openModal = (user = null) => {
        setError('');
        setShowPassword(false);
        setShowConfirmPassword(false);
        setChangePassword(false);
        if (user) {
            setEditingUser(user);
            setNewUser({
                username: user.username,
                password: '',
                confirmPassword: '',
                role: user.role,
                display_name: user.display_name || '',
                is_active: user.is_active === 1 || user.is_active === true
            });
        } else {
            setEditingUser(null);
            setNewUser({ username: '', password: '', confirmPassword: '', role: 'User', display_name: '', is_active: true });
        }
        setIsModalOpen(true);
    }

    // Filter and Sort Users
    const filteredUsers = users.filter(u => {
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            const matchUsername = u.username.toLowerCase().includes(lowerTerm);
            const matchDisplayName = (u.display_name || '').toLowerCase().includes(lowerTerm);
            if (!matchUsername && !matchDisplayName) return false;
        }

        if (filterRole !== 'All' && u.role !== filterRole) return false;

        if (filterStatus !== 'All') {
            const isActive = u.is_active === 1 || u.is_active === true;
            if (filterStatus === 'Active' && !isActive) return false;
            if (filterStatus === 'Inactive' && isActive) return false;
        }

        return true;
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (typeof aValue === 'string') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
                : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalItems = sortedUsers.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentUsers = sortedUsers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'users');
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    return (
        <div className="bg-slate-50 min-h-screen pb-10 text-slate-900">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">
                <div className="relative md:sticky md:top-[64px] z-40 bg-slate-50/80 pt-1.5 pb-2 mb-2 -mx-2 px-2 md:-mx-4 md:px-4 ">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                            <div className="relative w-full md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหาชื่อผู้ใช้ หรือชื่อแสดง..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 outline-none text-sm placeholder:text-slate-400"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="w-full md:w-auto">
                                <CustomFilterSelect
                                    label="บทบาท"
                                    icon={Shield}
                                    allLabel="ทั้งหมด"
                                    value={filterRole}
                                    onChange={setFilterRole}
                                    options={[
                                        { label: 'Admin', value: 'Admin' },
                                        { label: 'Staff', value: 'Staff' },
                                        { label: 'Guard', value: 'Guard' },
                                        { label: 'User', value: 'User' }
                                    ]}
                                />
                            </div>

                            <div className="w-full md:w-auto">
                                <CustomFilterSelect
                                    label="สถานะ"
                                    icon={Tag}
                                    allLabel="ทั้งหมด"
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    options={[
                                        { label: 'Active', value: 'Active' },
                                        { label: 'Inactive', value: 'Inactive' }
                                    ]}
                                />
                            </div>

                            <div className="ml-auto">
                                {canEdit && (
                                    <button
                                        onClick={() => openModal(null)}
                                        className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10 active:scale-95 border border-blue-500/20 "
                                    >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        <span className="truncate uppercase tracking-wide">เพิ่มผู้ใช้</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Users className="w-5 h-5 text-indigo-600" />
                            User Management
                        </h2>
                        <span className="text-[9px] font-bold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{filteredUsers.length} คน</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse table-fixed min-w-[1000px]">
                            <thead>
                                <tr>
                                    <th onClick={() => requestSort('id')} className="w-[60px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">ID{getSortIcon('id')}</div>
                                    </th>
                                    <th onClick={() => requestSort('username')} className="w-[150px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1 pl-4">Username{getSortIcon('username')}</div>
                                    </th>
                                    <th onClick={() => requestSort('display_name')} className="min-w-[150px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors text-slate-500">
                                        <div className="flex items-center gap-1">Display Name{getSortIcon('display_name')}</div>
                                    </th>
                                    <th onClick={() => requestSort('role')} className="w-[90px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">Role {getSortIcon('role')}</div>
                                    </th>
                                    <th className="w-auto"></th>
                                    <th onClick={() => requestSort('created_at')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่สร้าง{getSortIcon('created_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('updated_at')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่อัปเดต{getSortIcon('updated_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('is_active')} className="w-[90px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">สถานะ{getSortIcon('is_active')}</div>
                                    </th>
                                    {canEdit && <th className="w-[70px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-right pr-4"></th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={canEdit ? 9 : 8} className="p-20 text-center text-slate-500">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <div className="bg-slate-50 p-4 rounded-full">
                                                    <Users className="w-8 h-8 text-slate-300" />
                                                </div>
                                                <p className="font-bold text-sm text-slate-400">ไม่พบข้อมูลผู้ใช้งาน</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentUsers.map((u) => (
                                        <tr key={u.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex flex-col items-center justify-center text-center">
                                                    <span className="text-[11px] font-black text-slate-400 leading-none">{u.id}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle pl-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-black text-slate-700 leading-none">{u.username}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-black text-slate-700 leading-none">{u.display_name || <span className="text-slate-200">-</span>}</span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex items-center justify-center text-center">
                                                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-bold tracking-wide border  shadow-sm ${(u.role === 'Admin' || u.role === 'admin') ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                        u.role === 'Staff' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                            u.role === 'Guard' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                                'bg-blue-50 text-blue-600 border-blue-100'
                                                        }`}>
                                                        {u.role}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="w-auto"></td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex flex-col justify-center leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-tight">
                                                        {u.created_at ? new Date(u.created_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <div className="text-[8px] text-slate-400 mt-1 truncate max-w-[100px]">by {u.created_by_name || 'System'}</div>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex flex-col justify-center leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-tight">
                                                        {u.updated_at ? new Date(u.updated_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <div className="text-[8px] text-slate-400 mt-1 truncate max-w-[100px]">by {u.updated_by_name || '-'}</div>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                <div className="flex items-center justify-center text-center">
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wide border ${(u.is_active === 1 || u.is_active === true) ? 'bg-green-50 text-green-700 border-green-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                                                        }`}>
                                                        {(u.is_active === 1 || u.is_active === true) ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>
                                            {canEdit && (
                                                <td className="px-1 py-1 align-middle text-right pr-4">
                                                    <div className="flex justify-end items-center gap-1.5 pr-1">
                                                        <button
                                                            onClick={() => openModal(u)}
                                                            className="text-slate-300 hover:text-blue-500 transition-colors active:scale-95"
                                                            title="แก้ไข"
                                                        >
                                                            <Pencil size={13} strokeWidth={2.5} />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50 outline-none  focus:ring-2 focus:ring-blue-50"
                                    >
                                        <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                    </button>

                                    {isRowsDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                <div className="py-0.5">
                                                    {[10, 20, 50, 100].map(size => (
                                                        <button
                                                            key={size}
                                                            onClick={() => {
                                                                setRowsPerPage(size);
                                                                setIsRowsDropdownOpen(false);
                                                                setCurrentPage(1);
                                                            }}
                                                            className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                        >
                                                            <span>{size}</span>
                                                            {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                    {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems} รายการ
                                </span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="First Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Previous Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                </button>

                                <div className="flex items-center gap-1 px-2">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black  ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Next Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Last Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setIsModalOpen(false)}
                    />
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-200">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                    {editingUser ? 'แก้ไขผู้ใช้งาน' : 'เพิ่มผู้ใช้งานใหม่'}
                                </h2>
                                <p className="text-xs font-medium text-slate-400 mt-0.5">ระบุรายละเอียดข้อมูลส่วนตัวและสิทธิการใช้งาน</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600  font-bold"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                                    handleSave(e);
                                }
                            }}
                            className="flex flex-col flex-1 overflow-hidden"
                        >

                            <div className="p-4 space-y-4 overflow-y-auto flex-1">
                                {/* Section 1: User Info */}
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">ข้อมูลผู้ใช้งาน</h3>
                                    </div>
                                    <div className="space-y-2.5">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">ชื่อ-นามสกุล <span className="text-rose-500">*</span></label>
                                            <input
                                                className={`w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${error && !newUser.display_name.trim() ? 'border-rose-500' : 'border-slate-200'}`}
                                                value={newUser.display_name || ""}
                                                onChange={(e) => {
                                                    setNewUser({ ...newUser, display_name: e.target.value });
                                                    if (error) setError('');
                                                }}
                                                required
                                                placeholder="ชื่อ-นามสกุล"
                                            />
                                        </div>
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">Username <span className="text-rose-500">*</span></label>
                                            <input
                                                disabled={!!editingUser}
                                                className={`w-full h-9 text-base md:text-[13px] font-medium px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${editingUser ? 'bg-slate-100 text-slate-900 cursor-not-allowed border-slate-200' : `bg-white text-slate-700 ${error && (!newUser.username.trim() || newUser.username.trim().length < 4) ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                value={newUser.username}
                                                onChange={(e) => {
                                                    if (!editingUser) {
                                                        const val = e.target.value.replace(/[^a-zA-Z]/g, '');
                                                        setNewUser({ ...newUser, username: val });
                                                        if (error) setError('');
                                                    }
                                                }}
                                                required
                                                placeholder="Username สำหรับเข้าสู่ระบบ"
                                                autoComplete="one-time-code"
                                                name="usr_login_identifier"
                                            />
                                        </div>

                                        {/* Toggle for change password during editing */}
                                        {editingUser && (
                                            <div className="flex items-center justify-between bg-slate-50 border border-slate-200/60 p-3 rounded-xl mb-3 hover:bg-slate-100/40 transition-colors duration-150">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                                        <Lock size={15} />
                                                    </div>
                                                    <div>
                                                        <label className="text-[12px] font-bold text-slate-700 leading-none">ต้องการเปลี่ยนรหัสผ่านให้ผู้ใช้งานนี้ ?</label>
                                                        <p className="text-[10px] text-slate-400 mt-0.5 leading-none font-medium">กรุณาตั้งรหัสผ่านใหม่</p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const nextVal = !changePassword;
                                                        setChangePassword(nextVal);
                                                        if (!nextVal) {
                                                            setNewUser(prev => ({ ...prev, password: '', confirmPassword: '' }));
                                                            if (error) setError('');
                                                        }
                                                    }}
                                                    className={`w-9 h-5 rounded-full p-0.5 transition-colors duration-200 focus:outline-none flex items-center ${changePassword ? 'bg-indigo-600' : 'bg-slate-200'
                                                        }`}
                                                >
                                                    <div
                                                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${changePassword ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-0.5">
                                                <label className="text-[10px] font-bold text-slate-500 tracking-wider">
                                                    รหัสผ่าน {(!editingUser || changePassword) && <span className="text-rose-500">*</span>}
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={isFirefox ? (showPassword ? "text" : "password") : "text"}
                                                        style={isFirefox ? {} : { WebkitTextSecurity: showPassword ? 'none' : 'disc' }}
                                                        disabled={editingUser && !changePassword}
                                                        className={`w-full h-9 text-base md:text-[13px] font-medium pl-3 pr-9 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${editingUser && !changePassword
                                                            ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                                                            : `bg-white text-slate-700 ${error && (!editingUser && !newUser.password) ? 'border-rose-500' : (error && newUser.password !== newUser.confirmPassword ? 'border-rose-500' : 'border-slate-200')}`
                                                            }`}
                                                        value={newUser.password}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^a-zA-Z0-9!@#$%^&*]/g, '');
                                                            setNewUser({ ...newUser, password: val });
                                                            if (error) setError('');
                                                        }}
                                                        required={!editingUser || changePassword}
                                                        placeholder={editingUser && !changePassword ? "••••••••" : "รหัสผ่าน"}
                                                        autoComplete={isFirefox ? "new-password" : "off"}
                                                        name="user_secure_pass"
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={editingUser && !changePassword}
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 transition-colors rounded-full focus:outline-none flex items-center justify-center ${editingUser && !changePassword
                                                            ? 'text-slate-300 pointer-events-none'
                                                            : 'text-slate-400 hover:text-slate-600'
                                                            }`}
                                                    >
                                                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="space-y-0.5">
                                                <label className="text-[10px] font-bold text-slate-500 tracking-wider">
                                                    ยืนยันรหัสผ่าน {(!editingUser || changePassword) && <span className="text-rose-500">*</span>}
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={isFirefox ? (showConfirmPassword ? "text" : "password") : "text"}
                                                        style={isFirefox ? {} : { WebkitTextSecurity: showConfirmPassword ? 'none' : 'disc' }}
                                                        disabled={editingUser && !changePassword}
                                                        className={`w-full h-9 text-base md:text-[13px] font-medium pl-3 pr-9 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${editingUser && !changePassword
                                                            ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                                                            : `bg-white text-slate-700 ${error && (!editingUser && !newUser.confirmPassword) ? 'border-rose-500' : (error && newUser.password !== newUser.confirmPassword ? 'border-rose-500' : 'border-slate-200')}`
                                                            }`}
                                                        value={newUser.confirmPassword}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^a-zA-Z0-9!@#$%^&*]/g, '');
                                                            setNewUser({ ...newUser, confirmPassword: val });
                                                            if (error) setError('');
                                                        }}
                                                        required={!editingUser || changePassword}
                                                        placeholder={editingUser && !changePassword ? "••••••••" : "ยืนยันรหัสผ่าน"}
                                                        autoComplete={isFirefox ? "new-password" : "off"}
                                                        name="user_confirm_secure_pass"
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={editingUser && !changePassword}
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 transition-colors rounded-full focus:outline-none flex items-center justify-center ${editingUser && !changePassword
                                                            ? 'text-slate-300 pointer-events-none'
                                                            : 'text-slate-400 hover:text-slate-600'
                                                            }`}
                                                    >
                                                        {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                                    </button>
                                                </div>
                                                {newUser.confirmPassword && newUser.password !== newUser.confirmPassword && (
                                                    <p className="text-[10px] text-rose-500 font-medium flex items-center gap-1 mt-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                                        <AlertCircle className="w-3 h-3 text-rose-500 shrink-0" />
                                                        <span>รหัสผ่านไม่ตรงกัน</span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {(!editingUser || changePassword) && (() => {
                                            const passwordVal = newUser.password || '';
                                            const hasMinLength = passwordVal.length >= 8;
                                            const hasUppercase = /[A-Z]/.test(passwordVal);
                                            const hasLowercase = /[a-z]/.test(passwordVal);
                                            const hasNumber = /[0-9]/.test(passwordVal);
                                            const hasSpecial = /[!@#$%^&*]/.test(passwordVal);
                                            return (
                                                <div className="text-[10px] text-slate-500 mt-1.5 px-1 space-y-0.5 animate-in fade-in slide-in-from-top-1 duration-200">
                                                    <div className={`flex items-center gap-1 ${hasMinLength ? 'text-emerald-600 font-bold' : 'text-slate-500'}`}>
                                                        {hasMinLength ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 text-center text-slate-300 shrink-0">-</span>}
                                                        <span>รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร</span>
                                                    </div>
                                                    <div className={`flex items-center gap-1 ${hasUppercase ? 'text-emerald-600 font-bold' : 'text-slate-500'}`}>
                                                        {hasUppercase ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 text-center text-slate-300 shrink-0">-</span>}
                                                        <span>ตัวพิมพ์ใหญ่ (A-Z) อย่างน้อย 1 ตัว</span>
                                                    </div>
                                                    <div className={`flex items-center gap-1 ${hasLowercase ? 'text-emerald-600 font-bold' : 'text-slate-500'}`}>
                                                        {hasLowercase ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 text-center text-slate-300 shrink-0">-</span>}
                                                        <span>ตัวพิมพ์เล็ก (a-z) อย่างน้อย 1 ตัว</span>
                                                    </div>
                                                    <div className={`flex items-center gap-1 ${hasNumber ? 'text-emerald-600 font-bold' : 'text-slate-500'}`}>
                                                        {hasNumber ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 text-center text-slate-300 shrink-0">-</span>}
                                                        <span>ตัวเลข (0-9) อย่างน้อย 1 ตัว</span>
                                                    </div>
                                                    <div className={`flex items-center gap-1 ${hasSpecial ? 'text-emerald-600 font-bold' : 'text-slate-500'}`}>
                                                        {hasSpecial ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <span className="w-3 text-center text-slate-300 shrink-0">-</span>}
                                                        <span>อักขระพิเศษ (!, @, #, $, %, ^, &, *) อย่างน้อย 1 ตัว</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Section 2: Account settings */}
                                <div className="space-y-1 pt-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-indigo-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">สิทธิ์และสถานะการใช้งาน</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">บทบาท</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                                    className={`w-full h-9 bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left outline-none ${isRoleDropdownOpen ? 'border-slate-300' : 'hover:border-slate-300'}`}
                                                >
                                                    <span className={`text-base md:text-[13px] font-medium truncate flex-1 ${newUser.role ? 'text-slate-700' : 'text-slate-400'}`}>
                                                        {newUser.role || 'เลือกระดับผู้ใช้'}
                                                    </span>
                                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isRoleDropdownOpen ? "rotate-180 text-blue-500" : ""}`} />
                                                </button>
                                                {isRoleDropdownOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsRoleDropdownOpen(false)} />
                                                        <div className="absolute bottom-full left-0 mb-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                            <div className="py-1">
                                                                {["Admin", "Staff", "Guard", "User"].map(r => (
                                                                    <button
                                                                        key={r}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewUser({ ...newUser, role: r });
                                                                            setIsRoleDropdownOpen(false);
                                                                        }}
                                                                        className={`w-full text-left px-4 py-2 text-sm font-medium flex items-center justify-between transition-colors ${newUser.role === r ? "bg-blue-50 text-blue-600" : "text-slate-600 hover:bg-slate-50"}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px]">{r}</span>
                                                                        {newUser.role === r && <Check className="w-3.5 h-3.5 text-blue-600" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">สถานะ</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                                    className={`w-full h-9 bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left outline-none ${isStatusDropdownOpen ? 'border-slate-300' : 'hover:border-slate-300'}`}
                                                >
                                                    <span className={`text-base md:text-[13px] font-medium truncate flex-1 ${(newUser.is_active === 1 || newUser.is_active === true) ? "text-blue-600" : "text-slate-500"}`}>
                                                        {(newUser.is_active === 1 || newUser.is_active === true) ? "Active" : "Inactive"}
                                                    </span>
                                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isStatusDropdownOpen ? "rotate-180 text-blue-500" : ""}`} />
                                                </button>
                                                {isStatusDropdownOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsStatusDropdownOpen(false)} />
                                                        <div className="absolute bottom-full left-0 mb-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                            <div className="py-1">
                                                                {[
                                                                    { label: 'Active', value: true },
                                                                    { label: 'Inactive', value: false }
                                                                ].map(opt => (
                                                                    <button
                                                                        key={opt.label}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewUser({ ...newUser, is_active: opt.value });
                                                                            setIsStatusDropdownOpen(false);
                                                                        }}
                                                                        className={`w-full text-left px-4 py-2 text-sm font-medium flex items-center justify-between transition-colors ${(newUser.is_active === 1 || newUser.is_active === true) === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px]">{opt.label}</span>
                                                                        {(newUser.is_active === 1 || newUser.is_active === true) === opt.value && <Check className="w-3.5 h-3.5 text-blue-600" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message - Fixed at bottom */}
                            {error && (
                                <div className="mx-5 mb-4 p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-200">
                                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={18} strokeWidth={2.5} />
                                    </div>
                                    <span className="text-xs leading-tight font-medium">{error}</span>
                                </div>
                            )}

                            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100 border border-slate-200 active:scale-95 "
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95 "
                                >
                                    {editingUser ? "บันทึกแก้ไข" : "บันทึก"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
                                <Check className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-sm font-medium text-slate-400 mt-2">{successMessage}</p>
                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-2 bg-blue-600 text-white rounded-2xl font-bold text-[14px] tracking-wide active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-100"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showErrorPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 tracking-tight">ไม่สามารถทำรายการได้</h3>
                            <p className="text-sm font-medium text-slate-400 mt-2 whitespace-pre-wrap">{errorMessage}</p>
                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-2 bg-rose-500 text-white rounded-2xl font-bold text-[14px] tracking-wide active:scale-95 hover:bg-rose-600 shadow-lg shadow-rose-100"
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default UserManagement;
